// Durable, retrying upload queue for SMRT COP wizard photos.
//
// Why this exists: field crews capture 50-70 photos per site on poor
// connections. Photos must (a) never be lost if the app is backgrounded,
// killed, or offline, and (b) upload reliably without blocking capture. So on
// capture we persist the COMPRESSED blob to IndexedDB first, then upload in
// the background with bounded concurrency + exponential backoff. A durable
// record is removed only AFTER its blob reference has been saved into the
// site's state (the onUploaded handler resolves) — so an interrupted upload
// is always recoverable.
//
// Scope: the queue uploads photos for the CURRENTLY OPEN site only (set via
// setActiveSite). Items for other sites stay parked in IndexedDB and resume
// when that site is next opened — which also covers crash/restart recovery.
// Site-state persistence stays owned by the wizard component (a single
// writer), so the queue hands a completed blobName back through onUploaded
// rather than writing site-state itself (avoids racing the wizard's autosave).

const DB_NAME = "smrtcop";
const STORE = "photoQueue";
const DB_VERSION = 1;
const MAX_CONCURRENCY = 2; // locked decision: 2 parallel uploads
const BASE_BACKOFF_MS = 1000;
const MAX_BACKOFF_MS = 30_000;

export interface PendingPhoto {
  localId: string;
  siteId: string;
  fieldKey: string;
  fileName: string;
  blob: Blob;
  attempts: number;
  createdAt: number;
}

// Per-slot status surfaced to the wizard, keyed by fieldKey for the active
// site. "waiting" = offline or in backoff between retries.
export type PhotoSlotStatus = "queued" | "uploading" | "waiting";

interface ActiveHandlers {
  // Persist the blob reference (the queue does NOT touch site-state). Resolve
  // once saved; the durable record is removed only after this resolves.
  onUploaded: (item: PendingPhoto, blobName: string) => Promise<void>;
  // Latest per-slot statuses for the active site (keyed by fieldKey).
  onStatusChange: (statuses: Record<string, PhotoSlotStatus>) => void;
}

// ─── IndexedDB ──────────────────────────────────────────────────────────────

let dbPromise: Promise<IDBDatabase> | null = null;
function openDB(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const os = db.createObjectStore(STORE, { keyPath: "localId" });
        os.createIndex("siteId", "siteId", { unique: false });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

async function idbPut(item: PendingPhoto): Promise<void> {
  const db = await openDB();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).put(item);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

async function idbDelete(localId: string): Promise<void> {
  const db = await openDB();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).delete(localId);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

async function idbGetBySite(siteId: string): Promise<PendingPhoto[]> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readonly");
    const idx = tx.objectStore(STORE).index("siteId");
    const req = idx.getAll(IDBKeyRange.only(siteId));
    req.onsuccess = () => resolve((req.result as PendingPhoto[]) || []);
    req.onerror = () => reject(req.error);
  });
}

// ─── In-memory queue state (singleton) ───────────────────────────────────────

let activeSiteId: string | null = null;
let handlers: ActiveHandlers | null = null;
let queue: PendingPhoto[] = []; // pending items for the active site
const inFlight = new Set<string>(); // localIds currently uploading
const backingOff = new Set<string>(); // localIds waiting out a backoff timer
const slotStatus: Record<string, PhotoSlotStatus> = {}; // keyed by fieldKey
let online = typeof navigator !== "undefined" ? navigator.onLine : true;

function emitStatus(): void {
  handlers?.onStatusChange({ ...slotStatus });
}

function backendUrl(): string | undefined {
  return process.env.REACT_APP_BACKEND_URL;
}

export function newLocalId(): string {
  return `ph_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

// Open a site for uploading. Loads its parked items, drops any whose
// reference is already saved (savedKeys — recovers from a crash between
// upload-success and ref-save without re-uploading), and starts draining.
export async function setActiveSite(
  siteId: string | null,
  h: ActiveHandlers | null,
  savedKeys?: Set<string>,
): Promise<void> {
  activeSiteId = siteId;
  handlers = h;
  queue = [];
  inFlight.clear();
  backingOff.clear();
  Object.keys(slotStatus).forEach((k) => delete slotStatus[k]);

  if (!siteId || !h) return;

  const items = await idbGetBySite(siteId);
  for (const it of items) {
    if (savedKeys?.has(it.fieldKey)) {
      // Already uploaded AND its reference is persisted — drop the duplicate.
      await idbDelete(it.localId).catch(() => undefined);
      continue;
    }
    queue.push(it);
    slotStatus[it.fieldKey] = online ? "queued" : "waiting";
  }
  emitStatus();
  pump();
}

// Add a freshly-captured photo. Persists the blob durably first, then queues.
export async function enqueue(item: PendingPhoto): Promise<void> {
  await idbPut(item);
  if (item.siteId !== activeSiteId) return;
  // Replace any existing queued item for the same slot (a retake).
  queue = queue.filter((q) => q.fieldKey !== item.fieldKey);
  queue.push(item);
  slotStatus[item.fieldKey] = online ? "queued" : "waiting";
  emitStatus();
  pump();
}

// Drop any pending upload for a slot (user removed/retook before it landed).
export async function removePending(
  siteId: string,
  fieldKey: string,
): Promise<void> {
  const items = await idbGetBySite(siteId);
  for (const it of items) {
    if (it.fieldKey === fieldKey) await idbDelete(it.localId).catch(() => undefined);
  }
  queue = queue.filter((q) => q.fieldKey !== fieldKey);
  delete slotStatus[fieldKey];
  emitStatus();
}

function pump(): void {
  if (!online || !activeSiteId) return;
  while (inFlight.size < MAX_CONCURRENCY) {
    const next = queue.find(
      (it) => !inFlight.has(it.localId) && !backingOff.has(it.localId),
    );
    if (!next) break;
    void processItem(next);
  }
}

async function processItem(item: PendingPhoto): Promise<void> {
  inFlight.add(item.localId);
  slotStatus[item.fieldKey] = "uploading";
  emitStatus();

  try {
    const body = new FormData();
    body.append(
      "file",
      new File([item.blob], item.fileName, { type: "image/jpeg" }),
    );
    const res = await fetch(
      `${backendUrl()}/smrtcop/files/${encodeURIComponent(item.siteId)}/${encodeURIComponent(item.fieldKey)}`,
      { method: "POST", credentials: "include", body },
    );
    if (!res.ok) throw new Error(`upload ${res.status}`);
    const meta = (await res.json()) as { blobName: string };

    // Hand the reference to the wizard to persist; only then drop the durable
    // record, so a failure to save leaves the photo recoverable.
    if (item.siteId === activeSiteId && handlers) {
      await handlers.onUploaded(item, meta.blobName);
    }
    await idbDelete(item.localId).catch(() => undefined);

    inFlight.delete(item.localId);
    queue = queue.filter((q) => q.localId !== item.localId);
    delete slotStatus[item.fieldKey];
    emitStatus();
    pump();
  } catch (err) {
    inFlight.delete(item.localId);
    item.attempts += 1;
    await idbPut(item).catch(() => undefined);
    const q = queue.find((x) => x.localId === item.localId);
    if (q) q.attempts = item.attempts;
    slotStatus[item.fieldKey] = "waiting";
    emitStatus();
    console.warn(
      `[cop] photo upload failed (attempt ${item.attempts}):`,
      (err as Error).message,
    );

    if (!online) return; // 'online' event will resume

    // Exponential backoff with jitter, capped.
    const delay =
      Math.min(MAX_BACKOFF_MS, BASE_BACKOFF_MS * 2 ** Math.min(item.attempts, 5)) +
      Math.random() * 500;
    backingOff.add(item.localId);
    setTimeout(() => {
      backingOff.delete(item.localId);
      if (item.siteId === activeSiteId && queue.some((x) => x.localId === item.localId)) {
        slotStatus[item.fieldKey] = "queued";
        emitStatus();
        pump();
      }
    }, delay);
  }
}

if (typeof window !== "undefined") {
  window.addEventListener("online", () => {
    online = true;
    pump();
  });
  window.addEventListener("offline", () => {
    online = false;
  });
}

export const copPhotoQueue = {
  setActiveSite,
  enqueue,
  removePending,
  newLocalId,
};
